import React from 'react';

interface ProductCardCustomizationProps {
  formData: any;
  handleConfigChange: (path: string, value: any) => void;
  setPreviewValue: (path: string, value: any) => void;
}

export const ProductCardCustomization: React.FC<ProductCardCustomizationProps> = ({ 
  formData, 
  handleConfigChange, 
  setPreviewValue 
}) => {
  // Récupérer la config actuelle ou valeurs par défaut
  const config = formData?.config?.pages?.shop?.productCard || {};

  const handleChange = (key: string, value: any) => {
    handleConfigChange(`pages.shop.productCard.${key}`, value);
    setPreviewValue(`pages.shop.productCard.${key}`, value);
  };

  return (
    <div className="space-y-6">
      {/* Grille de produits */}
      <div className="bg-black/20 border border-white/10 rounded-lg p-4">
        <h5 className="text-white font-medium mb-4">📐 Grille de produits</h5>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Colonnes (Mobile)</label>
            <select
              value={config.gridColsMobile || '1'}
              onChange={(e) => handleChange('gridColsMobile', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
            >
              <option value="1">1 colonne</option>
              <option value="2">2 colonnes</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Colonnes (Tablet)</label>
            <select
              value={config.gridColsTablet || '2'}
              onChange={(e) => handleChange('gridColsTablet', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
            >
              <option value="2">2 colonnes</option>
              <option value="3">3 colonnes</option>
              <option value="4">4 colonnes</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Colonnes (Desktop)</label>
            <select
              value={config.gridColsDesktop || '3'}
              onChange={(e) => handleChange('gridColsDesktop', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
            >
              <option value="2">2 colonnes</option>
              <option value="3">3 colonnes</option>
              <option value="4">4 colonnes</option>
              <option value="5">5 colonnes</option>
            </select>
          </div>
          <div className="col-span-3">
            <label className="block text-xs text-gray-400 mb-1">Espacement (gap)</label>
            <input
              type="range"
              min="8"
              max="48"
              step="4"
              value={config.gridGap || '16'}
              onChange={(e) => handleChange('gridGap', e.target.value)}
              className="w-full"
            />
            <span className="text-xs text-gray-400">{config.gridGap || '16'}px</span>
          </div>
        </div>
      </div>

      {/* Style du conteneur */}
      <div className="bg-black/20 border border-white/10 rounded-lg p-4">
        <h5 className="text-white font-medium mb-4">🎨 Style du conteneur</h5>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Arrondi (border-radius)</label>
            <input
              type="range"
              min="0"
              max="32"
              step="2"
              value={config.borderRadius || '8'}
              onChange={(e) => handleChange('borderRadius', e.target.value)}
              className="w-full"
            />
            <span className="text-xs text-gray-400">{config.borderRadius || '8'}px</span>
          </div>
          
          <div>
            <label className="block text-xs text-gray-400 mb-1">Épaisseur bordure</label>
            <input
              type="range"
              min="0"
              max="4"
              step="1"
              value={config.borderWidth || '1'}
              onChange={(e) => handleChange('borderWidth', e.target.value)}
              className="w-full"
            />
            <span className="text-xs text-gray-400">{config.borderWidth || '1'}px</span>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Opacité fond</label>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={config.bgOpacity || '5'}
              onChange={(e) => handleChange('bgOpacity', e.target.value)}
              className="w-full"
            />
            <span className="text-xs text-gray-400">{config.bgOpacity || '5'}%</span>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Shadow (hover)</label>
            <select
              value={config.shadowSize || '2xl'}
              onChange={(e) => handleChange('shadowSize', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
            >
              <option value="none">Aucune</option>
              <option value="md">Petite</option>
              <option value="lg">Moyenne</option>
              <option value="xl">Grande</option>
              <option value="2xl">Très grande</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Scale (hover)</label>
            <input
              type="range"
              min="100"
              max="110"
              step="1"
              value={config.hoverScale || '102'}
              onChange={(e) => handleChange('hoverScale', e.target.value)}
              className="w-full"
            />
            <span className="text-xs text-gray-400">{(parseFloat(config.hoverScale || '102') / 100).toFixed(2)}</span>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Durée transition (ms)</label>
            <input
              type="range"
              min="100"
              max="1000"
              step="50"
              value={config.transitionDuration || '300'}
              onChange={(e) => handleChange('transitionDuration', e.target.value)}
              className="w-full"
            />
            <span className="text-xs text-gray-400">{config.transitionDuration || '300'}ms</span>
          </div>
        </div>
      </div>

      {/* Image du produit */}
      <div className="bg-black/20 border border-white/10 rounded-lg p-4">
        <h5 className="text-white font-medium mb-4">🖼️ Image du produit</h5>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Ratio d'aspect</label>
            <select
              value={config.imageAspectRatio || 'square'}
              onChange={(e) => handleChange('imageAspectRatio', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
            >
              <option value="square">Carré (1:1)</option>
              <option value="4/3">4:3 (classique)</option>
              <option value="16/9">16:9 (large)</option>
              <option value="3/4">Portrait (3:4)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Object-fit</label>
            <select
              value={config.imageObjectFit || 'cover'}
              onChange={(e) => handleChange('imageObjectFit', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
            >
              <option value="cover">Cover (remplir)</option>
              <option value="contain">Contain (contenir)</option>
              <option value="fill">Fill (étirer)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Zoom hover (%)</label>
            <input
              type="range"
              min="100"
              max="150"
              step="5"
              value={config.imageHoverScale || '110'}
              onChange={(e) => handleChange('imageHoverScale', e.target.value)}
              className="w-full"
            />
            <span className="text-xs text-gray-400">{config.imageHoverScale || '110'}%</span>
          </div>

          <div className="col-span-2 md:col-span-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={config.imageOverlay !== false}
                onChange={(e) => handleChange('imageOverlay', e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-400">Overlay dégradé sur hover</span>
            </label>
          </div>
        </div>
      </div>

      {/* Badge "Produit Unique" */}
      <div className="bg-black/20 border border-white/10 rounded-lg p-4">
        <h5 className="text-white font-medium mb-4">🏷️ Badge "Produit Unique"</h5>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Position</label>
            <select
              value={config.badgePosition || 'top-right'}
              onChange={(e) => handleChange('badgePosition', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
            >
              <option value="top-left">Haut gauche</option>
              <option value="top-right">Haut droite</option>
              <option value="bottom-left">Bas gauche</option>
              <option value="bottom-right">Bas droite</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Texte (unique)</label>
            <input
              type="text"
              value={config.badgeTextUnique || 'Produit Unique'}
              onChange={(e) => handleChange('badgeTextUnique', e.target.value)}
              placeholder="Produit Unique"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Texte (limité)</label>
            <input
              type="text"
              value={config.badgeTextLimited || 'Édition Limitée'}
              onChange={(e) => handleChange('badgeTextLimited', e.target.value)}
              placeholder="Édition Limitée"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
            />
          </div>

          <div className="col-span-2 md:col-span-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={config.badgeShow !== false}
                onChange={(e) => handleChange('badgeShow', e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-400">Afficher le badge</span>
            </label>
          </div>
        </div>
      </div>

      {/* Boutons d'action */}
      <div className="bg-black/20 border border-white/10 rounded-lg p-4">
        <h5 className="text-white font-medium mb-4">🔘 Boutons d'action</h5>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Position verticale</label>
            <select
              value={config.buttonsPosition || 'center'}
              onChange={(e) => handleChange('buttonsPosition', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
            >
              <option value="top">Haut de l'image</option>
              <option value="center">Centre de l'image</option>
              <option value="bottom">Bas de l'image</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Espacement</label>
            <input
              type="range"
              min="4"
              max="24"
              step="2"
              value={config.buttonsGap || '12'}
              onChange={(e) => handleChange('buttonsGap', e.target.value)}
              className="w-full"
            />
            <span className="text-xs text-gray-400">{config.buttonsGap || '12'}px</span>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Taille icônes</label>
            <input
              type="range"
              min="14"
              max="28"
              step="2"
              value={config.buttonsIconSize || '18'}
              onChange={(e) => handleChange('buttonsIconSize', e.target.value)}
              className="w-full"
            />
            <span className="text-xs text-gray-400">{config.buttonsIconSize || '18'}px</span>
          </div>

          <div className="col-span-2 md:col-span-3">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={config.buttonsShowOnHover !== false}
                onChange={(e) => handleChange('buttonsShowOnHover', e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-400">Afficher uniquement au survol</span>
            </label>
          </div>
        </div>
      </div>

      {/* Section informations */}
      <div className="bg-black/20 border border-white/10 rounded-lg p-4">
        <h5 className="text-white font-medium mb-4">📝 Section informations</h5>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Padding</label>
            <input
              type="range"
              min="8"
              max="32"
              step="2"
              value={config.infoPadding || '16'}
              onChange={(e) => handleChange('infoPadding', e.target.value)}
              className="w-full"
            />
            <span className="text-xs text-gray-400">{config.infoPadding || '16'}px</span>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Opacité fond</label>
            <input
              type="range"
              min="0"
              max="20"
              step="1"
              value={config.infoBgOpacity || '5'}
              onChange={(e) => handleChange('infoBgOpacity', e.target.value)}
              className="w-full"
            />
            <span className="text-xs text-gray-400">{config.infoBgOpacity || '5'}%</span>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Alignement texte</label>
            <select
              value={config.infoTextAlign || 'left'}
              onChange={(e) => handleChange('infoTextAlign', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
            >
              <option value="left">Gauche</option>
              <option value="center">Centre</option>
              <option value="right">Droite</option>
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">Taille titre</label>
            <select
              value={config.titleSize || 'xl'}
              onChange={(e) => handleChange('titleSize', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
            >
              <option value="lg">Grande (18px)</option>
              <option value="xl">Très grande (20px)</option>
              <option value="2xl">Extra grande (24px)</option>
            </select>
          </div>

          <div className="col-span-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={config.showStock !== false}
                onChange={(e) => handleChange('showStock', e.target.checked)}
                className="rounded"
              />
              <span className="text-sm text-gray-400">Afficher le stock disponible</span>
            </label>
          </div>
        </div>
      </div>

      {/* Layout alternatif */}
      <div className="bg-black/20 border border-white/10 rounded-lg p-4">
        <h5 className="text-white font-medium mb-4">🎭 Styles créatifs</h5>
        <div className="space-y-3">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={config.glassEffect === true}
              onChange={(e) => handleChange('glassEffect', e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-400">Effet verre (glassmorphism)</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={config.neonBorder === true}
              onChange={(e) => handleChange('neonBorder', e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-400">Bordure néon au survol</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={config.floatingAnimation === true}
              onChange={(e) => handleChange('floatingAnimation', e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-400">Animation flottante (floating)</span>
          </label>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={config.tiltEffect === true}
              onChange={(e) => handleChange('tiltEffect', e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-400">Effet d'inclinaison 3D (tilt)</span>
          </label>
        </div>
      </div>
    </div>
  );
};
